import { map } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { PagedResults } from '../models/paged-results/paged-results.model';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ApiService } from './api.service';
import { ClientConsultNoteTask } from '../models/client-consult-note-task.model';
import { NextQueueItemProvider } from '../interfaces/next-queue-item-provider';
import { NextQueueItem } from '../models/next-queue-item.model';
import { UserSettings } from '../enums/user-settings.enum';
import { QueueProcessingService } from './queue-processing.service';
import { LimitOffsetPagingMeta, Sort, Paging } from '../models/paged-results/limit-offset-paging.model';
import { TaskFilters, taskFiltersToURLSearchParams } from '../models/user-settings/task-settings.model';
import { ClientTaskHistoryItem } from '../models/client-task-history-item.model';

export class PagedClientConsultNoteTasks implements PagedResults {
  clientConsultNoteTasks: ClientConsultNoteTask[];
  meta: LimitOffsetPagingMeta;

  get records() {
    return this.clientConsultNoteTasks;
  }
}

const LegacySortColumn = 'needsConsultNote';

class NextClientConsultNoteTaskProvider implements NextQueueItemProvider {
  constructor(private clientConsultNoteTasksService: ClientConsultNoteTasksService) {}

  nextItemInQueueRequest(currentPage: number, pageSize: number, filters: TaskFilters) {
    return this.nextClientTaskRequest(currentPage, pageSize, filters);
  }

  nextItemFromStartRequest() {
    return this.nextClientTaskRequest(1, 1, null);
  }

  private nextClientTaskRequest(currentPage: number, pageSize: number, filters: TaskFilters) {
    const paging = { page: currentPage, pageSize };

    return this.clientConsultNoteTasksService.getList(paging, filters).pipe(
      map(resultsData => {
        const results = new PagedClientConsultNoteTasks();
        return Object.assign(results, resultsData);
      })
    );
  }
}

@Injectable()
export class ClientConsultNoteTasksService extends ApiService {
  private http = inject(HttpClient);
  private queueProcessingService = inject(QueueProcessingService);


  get(id: number): Observable<ClientConsultNoteTask> {
    return this.http.get<ClientConsultNoteTask>(`${environment.captureApi.url}/client_consult_note_tasks/${id}`);
  }

  getList(
    paging: Paging,
    filters: TaskFilters = null,
    sort: Sort = null
  ): Observable<PagedClientConsultNoteTasks> {
    const searchParams = taskFiltersToURLSearchParams(filters, paging, sort, LegacySortColumn);
    return this.http.get<PagedClientConsultNoteTasks>(
      `${environment.captureApi.url}/client_consult_note_tasks?${searchParams.toString()}`
    );
  }

  getSources(): Observable<string[]> {
    return this.http.get<string[]>(`${environment.captureApi.url}/client_consult_note_tasks/sources`);
  }

  getManufacturers(): Observable<string[]> {
    return this.http.get<string[]>(`${environment.captureApi.url}/client_consult_note_tasks/manufacturers`);
  }

  update(id: number, attributes: Record<string, unknown>): Observable<ClientConsultNoteTask> {
    return this.http.patch<ClientConsultNoteTask>(`${environment.captureApi.url}/client_consult_note_tasks/${id}`, {
      task: attributes,
    });
  }

  updateChoices(id: number, choice: string, choiceReasons: string[]): Observable<ClientConsultNoteTask> {
    return this.update(id, { choice, choiceReasons });
  }

  updateNotes(id: number, notes: string): Observable<ClientConsultNoteTask> {
    return this.update(id, { notes });
  }

  updateRejectedPrescription(id: number, rejectedPrescriptionIds: number[]): Observable<ClientConsultNoteTask> {
    return this.update(id, { rejectedPrescriptionIds });
  }

  submit(id: number): Observable<ClientConsultNoteTask> {
    return this.http.patch<ClientConsultNoteTask>(
      `${environment.captureApi.url}/client_consult_note_tasks/${id}/submit`,
      {}
    );
  }

  skipToNextTask(currentTask: ClientConsultNoteTask): Observable<NextQueueItem<ClientConsultNoteTask>> {
    const provider = new NextClientConsultNoteTaskProvider(this);
    return this.queueProcessingService.skipToNextItem<ClientConsultNoteTask>(
      UserSettings.clientPortalConsultNoteTaskQueueSettings,
      provider,
      currentTask
    );
  }

  getClientTaskHistoryItems(id: number): Observable<ClientTaskHistoryItem[]> {
    return this.http.get<ClientTaskHistoryItem[]>(
      `${environment.captureApi.url}/client_consult_note_tasks/${id}/history_items`
    );
  }
}
